/** @jsxImportSource @emotion/react */
import * as React from "react";
import { useSelector } from "react-redux";
import * as yup from "yup";
import * as authSelectors from "../auth/authSelectors";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import * as Inputs from "../common/hook-form/index";
import { routes } from "../../appRoutingHelpers";
import { SpinnerBlock } from "../common/Spinner";
import { colors } from "../../components/colors";
import { PrimaryButton } from "../../components/Buttons";
import { useGetCountries } from "../../service/api";
import { StyledText } from "../../assets/typography/typography";
import { FormRow } from "../common/hook-form/index";
import { GoBackLinkButton } from "../../components/GoBackLinkButton";
import { getCountrySpecificValues } from "../../util";
import { FileUploadInput } from "../../components/forms/inputs/FileUploadInput";
import { BaseUploadPreview } from "../../components/BaseUploadPreview";
import { css } from "@emotion/react";
import { ActionSpinner } from "../../components/ActionSpinner";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  useArchiveProductCategory,
  useCreateProductCategory,
  useGetProductCategory,
  useUpdateProductCategory,
  useUploadProductCategoryImage,
} from "./service/product-categories-api";
import { toastr } from "react-redux-toastr";
import { DeleteButton } from "../../components/NewButtons";
import { useHandleFetchError, getErrorMessage } from "../../error-handler";
import { ButtonAction } from "../../components/NewButtons";

interface Props {}

const imageContainerStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const validationSchema = yup.object({
  name: yup.string().required("Name is required"),
  title: yup.string().required("Display name is required"),
});

const ProductCategoryUpsert: React.FC<Props> = (props) => {
  const { countryCode } = getCountrySpecificValues();
  const formMethods = useForm({
    defaultValues: {
      countryCode,
      imageUrl: "",
      files: [],
    },
    resolver: yupResolver(validationSchema),
  });
  const { handleSubmit, reset } = formMethods;
  let { id } = useParams<{ id?: string }>();

  const navigate = useNavigate();
  const isEdit = !!id;

  const selectedOrganizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );

  const organization =
    useSelector(authSelectors.selectedOrganizationKeyName) || "";

  const {
    data: productCategory,
    isLoading: isLoadingProductCategory,
    error: getProductCategoryError,
  } = useGetProductCategory(organization, id);

  const {
    data: countries,
    isLoading: countriesAreLoading,
    error: listCountriesError,
  } = useGetCountries();

  const {
    mutateAsync: uploadProductCategoryImage,
  } = useUploadProductCategoryImage();

  const countryOptions = (countries || [])
    .filter((c) => c.enabled)
    .sort()
    .map((o) => ({
      label: o.englishName,
      value: o.countryCode,
    }));

  countryOptions?.unshift({
    label: "",
    value: "",
  });

  const isLoading = isLoadingProductCategory || countriesAreLoading;

  const {
    mutateAsync: archiveProductCategory,
    isLoading: isLoadingArchiveProductCategory,
  } = useArchiveProductCategory();

  const {
    mutateAsync: createProductCategory,
    isLoading: isLoadingCreateProductCategory,
  } = useCreateProductCategory();
  const {
    mutateAsync: updateProductCategory,
    isLoading: isLoadingUpdateProductCategory,
  } = useUpdateProductCategory(id);

  const onSubmit = async (data: any) => {
    const { imageUrl, ...rest } = data;
    try {
      if (isEdit) {
        let newImageUrl = imageUrl;
        if (rest.files && rest.files.length > 0) {
          newImageUrl = await uploadProductCategoryImage({
            file: data.files[0],
            id,
            organizationKeyName: organization,
          });
        }
        await updateProductCategory({
          data: {
            id,
            ...data,
            ...(newImageUrl ? { imageUrl: newImageUrl } : { imageUrl: null }),
          },
          organizationKeyName: organization,
        });
      } else {
        const categoryId = await createProductCategory({
          data: rest,
          organizationKeyName: organization,
        });
        if (rest.files && rest.files.length > 0) {
          const newCategoryImageUrl = await uploadProductCategoryImage({
            file: rest.files[0],
            id: categoryId,
            organizationKeyName: organization,
          });
          await updateProductCategory({
            data: {
              id: categoryId,
              ...rest,
              ...(newCategoryImageUrl ? { imageUrl: newCategoryImageUrl } : {}),
            },
            organizationKeyName: organization,
          });
        }
      }
      toastr.success(
        "Success",
        `Product category ${isEdit ? "updated" : "created"} successfully`
      );
      navigate(routes.shopProductCategoryList(organization));
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
    }
  };

  React.useEffect(() => {
    if (isEdit && productCategory) {
      reset(productCategory);
    }
  }, [isEdit, productCategory, reset]);

  useHandleFetchError(getProductCategoryError);
  useHandleFetchError(listCountriesError);

  if (isLoading) {
    return <SpinnerBlock />;
  }

  return (
    <FormProvider {...formMethods}>
      <div className="container mt-5">
        <GoBackLinkButton
          to={routes.shopProductCategoryList(selectedOrganizationKeyName)}
          text="Back to all product categories"
        />
        <div className="row">
          <div className="col-12">
            <form className="card" onSubmit={handleSubmit(onSubmit)}>
              <div className="card-header">
                <StyledText
                  color={colors.textPrimary}
                  className="bodyHeaderOneRegular card-title"
                >
                  {isEdit ? (
                    <>Product category edit</>
                  ) : (
                    <>New product category</>
                  )}
                </StyledText>
                {isEdit && (
                  <div className="card-options">
                    <PrimaryButton
                      type="button"
                      color="white"
                      backgroundColor={colors.errorDark}
                      hoverColor={colors.errorMedium}
                      disabled={isLoadingArchiveProductCategory}
                      onClick={async () => {
                        if (
                          !window.confirm(
                            `🛑 Are you sure you want to proceed?\n${productCategory?.title} will be deleted and no longer be visible in apps and on web.`
                          )
                        ) {
                          return;
                        }
                        try {
                          await archiveProductCategory({
                            data: { id: id! },
                            organizationKeyName: organization,
                          });
                          toastr.success(
                            "Success",
                            `Product category deleted successfully`
                          );
                          navigate(
                            routes.shopProductCategoryList(organization)
                          );
                        } catch (error: unknown) {
                          const message = getErrorMessage(error);
                          toastr.error("Error", message);
                        }
                      }}
                    >
                      {isLoadingArchiveProductCategory ? (
                        <ActionSpinner />
                      ) : (
                        "Delete"
                      )}
                    </PrimaryButton>
                  </div>
                )}
              </div>
              <div className="card-body">
                <div>
                  <StyledText
                    color={colors.textPrimary}
                    className="bodyHeaderOneSemi mb-2"
                  >
                    Standard product category information
                  </StyledText>
                  <StyledText
                    color={colors.textPrimary}
                    className="bodyTextRegular mb-6"
                  >
                    Below is the standard required information to create a
                    product category. You cannot create a product category if
                    the fields are not filled.
                  </StyledText>
                </div>
                <FormRow>
                  <Inputs.InputFormField
                    errors={formMethods.formState.errors}
                    register={formMethods.register}
                    label="Admin name"
                    name="name"
                    helpText="Only visible for admins."
                  />
                  <Inputs.InputFormField
                    errors={formMethods.formState.errors}
                    register={formMethods.register}
                    label="Product category display name"
                    name="title"
                    helpText="Visible in app by users."
                  />
                </FormRow>
                <FormRow>
                  <Inputs.TextareaFormField
                    errors={formMethods.formState.errors}
                    register={formMethods.register}
                    label="Description"
                    name="description"
                    helpText="Write a description of the product category here. E.g. for breakfast: “Breakfast is only available from 08:00 to 13:00” or “All burgers are served with a side of fries” etc."
                  />
                </FormRow>
                <div className="mt-1">
                  <StyledText
                    color={colors.textPrimary}
                    className="bodyHeaderOneSemi mb-2"
                  >
                    Product category image
                  </StyledText>
                  <StyledText
                    color={colors.textPrimary}
                    className="bodyTextRegular mb-6"
                  >
                    Upload image for the product category.
                  </StyledText>
                </div>
                <div css={imageContainerStyle}>
                  {formMethods.watch("imageUrl") && (
                    <>
                      <BaseUploadPreview url={formMethods.watch("imageUrl")} />
                      <DeleteButton
                        onClick={() => {
                          formMethods.setValue("imageUrl", "");
                        }}
                      >
                        Remove image
                      </DeleteButton>
                    </>
                  )}
                  <FileUploadInput multiple={false} />
                </div>
                <div className="mt-4">
                  <StyledText
                    color={colors.textPrimary}
                    className="bodyHeaderOneSemi mb-2"
                  >
                    Product category configuration
                  </StyledText>
                  <StyledText
                    color={colors.textPrimary}
                    className="bodyTextRegular mb-6"
                  >
                    Configure the product category below. Choose in what hours
                    the product category should be available for users in app
                    and on website.
                  </StyledText>
                </div>
                <Inputs.SelectFormField
                  errors={formMethods.formState.errors}
                  register={formMethods.register}
                  label="Country"
                  name="countryCode"
                  disabled={isEdit}
                  clearErrors={formMethods.clearErrors}
                  options={countryOptions}
                />
                <FormRow>
                  <Inputs.InputFormField
                    label={"Available from"}
                    name={"availabilityHours[0].open"}
                    errors={formMethods.formState.errors}
                    register={formMethods.register}
                    helpText={
                      "Open - e.g. 11:00. Leave empty if always available"
                    }
                  />
                  <Inputs.InputFormField
                    label={"Available to"}
                    name={"availabilityHours[0].close"}
                    errors={formMethods.formState.errors}
                    register={formMethods.register}
                    helpText={
                      "Close - e.g. 15:00. Leave empty if always available"
                    }
                  />
                </FormRow>
              </div>
              <div className="card-footer d-flex justify-content-end">
                <ButtonAction
                  disabled={
                    isLoadingCreateProductCategory ||
                    isLoadingUpdateProductCategory
                  }
                  type="submit"
                >
                  {isLoadingCreateProductCategory ||
                  isLoadingUpdateProductCategory ? (
                    <ActionSpinner />
                  ) : isEdit ? (
                    "Save"
                  ) : (
                    "Create"
                  )}
                </ButtonAction>
              </div>
            </form>
          </div>
        </div>
      </div>
    </FormProvider>
  );
};

export default ProductCategoryUpsert;
